import { z } from 'zod';

import { metadata } from '@nesso/common-contracts/schema-metadata';

// Colon-separated path (`session:generate`), deliberately not an enum — plans declare their
// own limits in the SSM catalog, so the vocabulary is open-ended.
export const billableActionSchema = z.string().min(1);

export const SESSION_GENERATE_ACTION = 'session:generate';

// duplicated from the owning domain package (contracts stay domain-free);
// subscription/application/src/enum-sync.ts asserts they stay in lockstep
export const SUBSCRIPTION_STATUSES = ['TRIALING', 'ACTIVE', 'PAST_DUE', 'CANCELED'] as const;

export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

// duplicated in practice-contracts (contracts may not cross-import); practice/application/src/enum-sync.ts
// asserts they stay in lockstep
export const SUBSCRIPTION_PRODUCTS = ['EHR', 'NOTES'] as const;

export const subscriptionProductSchema = z.enum(SUBSCRIPTION_PRODUCTS);
export type SubscriptionProduct = z.infer<typeof subscriptionProductSchema>;

export const cancellationFeedbackSchema = z.enum([
    'customer_service',
    'low_quality',
    'missing_features',
    'other',
    'switched_service',
    'too_complex',
    'too_expensive',
    'unused',
]);
export type CancellationFeedback = z.infer<typeof cancellationFeedbackSchema>;

export const cardSchema = z
    .object({
        brand: z.string().trim().min(1).meta(metadata.nonSensitive),
        last4: z
            .string()
            .regex(/^\d{4}$/, 'must be exactly 4 digits')
            .meta(metadata.nonSensitive),
        // Null-defaulted: messages published before the expiration existed carry neither part.
        expMonth: z
            .number()
            .int()
            .min(1)
            .max(12)
            .nullable()
            .default(null)
            .meta(metadata.nonSensitive),
        expYear: z
            .number()
            .int()
            .min(1000)
            .max(9999)
            .nullable()
            .default(null)
            .meta(metadata.nonSensitive),
    })
    // The domain and the subscriptions table both hold this. Without it here, one part alone
    // clears the boundary and throws deeper in, where a queued message is retried to the DLQ
    // rather than rejected with a reason.
    .refine(
        ({ expMonth, expYear }) => (expMonth === null) === (expYear === null),
        'card expiration must carry both month and year or neither',
    );

export type Card = z.infer<typeof cardSchema>;
